import { useState } from 'react'
import { useAccounts } from '@/features/transactions/hooks'
import { formatAmount } from '@/features/transactions/format'
import { ScheduledForm } from './ScheduledForm'
import { useDeleteSchedule, useSaveSchedule, useSchedules } from './hooks'
import type { ScheduledTransaction } from './types'

function signedAmount(s: ScheduledTransaction): number {
  const m = Math.abs(s.amount)
  return s.transactionType === 'income' ? m : -m
}

export function ScheduledScreen() {
  const schedules = useSchedules()
  const accounts = useAccounts()
  const save = useSaveSchedule()
  const remove = useDeleteSchedule()
  const [adding, setAdding] = useState(false)

  const accountName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? '—'

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Scheduled</h1>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-md bg-zinc-900 px-3 py-1.5 text-sm text-white dark:bg-zinc-100 dark:text-zinc-900"
          >
            New schedule
          </button>
        )}
      </div>

      {adding && (
        <ScheduledForm
          busy={save.isPending}
          onCancel={() => setAdding(false)}
          onSave={(s) => save.mutate(s, { onSuccess: () => setAdding(false) })}
        />
      )}

      {schedules.isLoading && <p className="text-sm text-zinc-500">Loading…</p>}
      {schedules.data?.length === 0 && !adding && (
        <p className="text-sm text-zinc-500">
          No scheduled transactions yet. They fire automatically on or after their date.
        </p>
      )}

      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {schedules.data?.map((s) => {
          const amt = signedAmount(s)
          return (
            <li key={s.id} className="flex items-center gap-3 py-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={s.isEnabled}
                  onChange={(e) =>
                    save.mutate({ ...s, isEnabled: e.target.checked, updatedAt: new Date().toISOString() })
                  }
                />
                <span className="sr-only">Enabled</span>
              </label>
              <span className="flex-1 truncate">
                <span className={s.isEnabled ? '' : 'text-zinc-400 line-through'}>
                  {s.payeeName ?? 'No payee'}
                </span>
                <span className="ml-2 text-xs text-zinc-500">
                  {accountName(s.accountId)} · {s.frequency} · next{' '}
                  {new Date(s.nextOccurrence).toLocaleDateString()}
                </span>
              </span>
              <span className={`tabular-nums ${amt < 0 ? '' : 'text-funded'}`}>{formatAmount(amt)}</span>
              <button
                type="button"
                onClick={() => remove.mutate(s.id)}
                className="rounded-md px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-zinc-800"
              >
                Delete
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
